import React from 'react';
import { Button, Badge, IconButton, Menu } from '@parsina/framework';
import { ArrowRight, MoreHorizontal, Check, Loader2, TriangleAlert } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation, MIRROR_ICON } from '../../i18n/I18nProvider.jsx';
import { PracticeBadge } from '../../components/common/PracticeNotice.jsx';
import { SaveState } from '../../hooks/useEncounter.js';
import { elapsedParts } from './Visit.shared.js';

// The visit's own chrome: back, who, which visit, how long, whether it is saved,
// and the two things that end it.
//
// ── The clock is live, and it ticks once a minute ────────────────────────
//
// A visit's elapsed time is read in minutes; a per-second interval would repaint
// the header sixty times for every number that changes. It is derived from
// `startedAtMs` on each tick rather than counted up, so a tab that was asleep
// for an hour shows an hour rather than the seconds it was awake for.
//
// ── The autosave indicator is a control when it is red ───────────────────
//
// «ذخیره شد» and «در حال ذخیره» are status text. «ذخیره نشد» is a BUTTON: the
// next keystroke retries anyway (core.md §7.5), but a student who has stopped
// typing and can see a red word needs something to press that is not «پایان
// ویزیت».

function SaveIndicator({ state, onRetry }) {
  const { t } = useTranslation();
  if (state === SaveState.IDLE) return null;

  if (state === SaveState.ERROR) {
    return (
      <button
        type="button"
        onClick={onRetry}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, border: 0, background: 'none',
          font: 'inherit', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: C.red,
        }}
        data-testid="autosave-error"
      >
        <TriangleAlert size={13} aria-hidden />
        {t('visit.save.failed')}
      </button>
    );
  }

  const saving = state === SaveState.SAVING;
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: CT.mute }}
      data-testid={saving ? 'autosave-saving' : 'autosave-saved'}
    >
      {saving ? <Loader2 size={13} aria-hidden /> : <Check size={13} aria-hidden color={C.teal} />}
      {t(saving ? 'visit.save.saving' : 'visit.save.saved')}
    </span>
  );
}

export default function VisitHeader({
  encounter,
  patient,
  saveState,
  onRetrySave,
  onBack,
  onEnd,
  onReopen,
  onCancel,
  readOnly,
  compact = false,
}) {
  const { t, formatNumber, formatDate } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuAnchor = React.useRef(null);
  const [, tick] = React.useState(0);

  React.useEffect(() => {
    if (readOnly) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, [readOnly]);

  const elapsed = elapsedParts(encounter?.startedAtMs);

  return (
    <header
      style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: compact ? '8px 12px' : '10px 16px',
        borderBottom: `1px solid ${C.border}`, background: C.card,
      }}
      data-testid="visit-header"
    >
      <IconButton
        size={36}
        variant="ghost"
        icon={<ArrowRight size={18} aria-hidden style={MIRROR_ICON} />}
        label={t('visit.back')}
        onClick={onBack}
        data-testid="visit-back"
      />

      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14, color: C.textPrimary }}>
          {patient?.displayName || patient?.title || ''}
        </strong>
        <PracticeBadge short />
        <Badge variant="outline" data-testid="visit-seq">
          {t('visit.number', { n: formatNumber(encounter?.seq || 0) })}
        </Badge>
      </span>

      {readOnly ? (
        <span style={{ fontSize: 12, color: CT.mute }} data-testid="visit-closed-at">
          {t('visit.closedAt', {
            date: encounter?.endedAtMs ? formatDate(Number(encounter.endedAtMs)) : '',
          })}
        </span>
      ) : (
        <span style={{ fontSize: 12, color: CT.mute }} data-testid="visit-elapsed">
          {t('visit.elapsed', {
            h: formatNumber(elapsed.hours),
            m: formatNumber(elapsed.minutes),
          })}
        </span>
      )}

      <SaveIndicator state={saveState} onRetry={onRetrySave} />

      <span style={{ marginInlineStart: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {readOnly ? (
          <Button size="sm" onClick={onReopen} data-testid="visit-reopen">{t('visit.reopen')}</Button>
        ) : (
          <Button size="sm" onClick={onEnd} data-testid="visit-end">{t('visit.end')}</Button>
        )}
        <span ref={menuAnchor}>
          <IconButton
            size={36}
            variant="ghost"
            icon={<MoreHorizontal size={18} aria-hidden />}
            label={t('common.actions.more')}
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="visit-more"
          />
        </span>
        <Menu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          anchorRef={menuAnchor}
          ariaLabel={t('common.actions.more')}
          width={220}
          data-testid="visit-menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setMenuOpen(false); onCancel(); }}
            style={{
              display: 'block', width: '100%', textAlign: 'start', border: 0, background: 'none',
              font: 'inherit', cursor: 'pointer', padding: '9px 12px', color: C.red, fontSize: 13,
            }}
            data-testid="visit-cancel"
          >
            {t('visit.cancel')}
          </button>
        </Menu>
      </span>
    </header>
  );
}
